import { Interpolation, Theme } from "@emotion/react";
import palette from "../palette";
import { ButtonProps, colors } from "@mui/material";

const defaultStyle = {
  fontSize: "14px",
  lineHeight: "24px",
  fontWeight: 600,
  cursor: "pointer",
  gap: "8px",
  borderRadius: "4px",
  outline: "none",
  padding: "5px 16px",
  border: "1px solid transparent",
  boxShadow: "unset",
};
const smSize = {
  fontSize: "14px",
  lineHeight: "18px",
  padding: "4px 12px",
  borderRadius: "4px",
  minHeight: "28px",
};
const mdSize = {
  fontSize: "14px",
  lineHeight: "18px",
  padding: "6px 16px",
  borderRadius: "4px",
  minHeight: "32px",
};
const lgSize = {
  minHeight: "36px",
};
const xlSize = {
  padding: "9px 20px",
  minHeight: "44px",
};
const xxlSize = {
  padding: "11px 24px",
  minHeight: "48px",
};
const styleDisabled = {
  backgroundColor: palette.disabled.main,
  color: palette.text.disabled,
  cursor: "default",
  borderColor: palette.disabled.main,
};

const variants:
  | {
      props:
        | Partial<ButtonProps>
        | ((
            props: Partial<ButtonProps> & {
              ownerState: Partial<ButtonProps>;
            }
          ) => boolean);
      style: Interpolation<{ theme: Theme }>;
    }[]
  | undefined = [
  {
    props: { variant: "contained" },
    style: {},
  },
  {
    props: { variant: "contained", color: "normal" },
    style: {
      backgroundColor: palette.primary.main,
      color: palette.text.white,
    },
  },
  {
    props: { variant: "contained", color: "primary" },
    style: {
      backgroundColor: palette.primary.main,
      color: palette.text.white,
      borderColor: palette.primary.main,
      "&:hover": {
        backgroundColor: palette.primary.hover,
        borderColor: palette.primary.hover,
      },
      "&:active": {
        backgroundColor: palette.primary.active,
        borderColor: palette.primary.active,
      },
    },
  },
  {
    props: { variant: "contained", color: "darkgreen" },
    style: {
      backgroundColor: palette.darkgreen.main,
      color: palette.text.white,
      borderColor: palette.darkgreen.main,
      "&:hover": {
        backgroundColor: palette.darkgreen.hover,
        borderColor: palette.darkgreen.hover,
      },
      "&:active": {
        backgroundColor: palette.darkgreen.active,
        borderColor: palette.darkgreen.active,
      },
    },
  },
  {
    props: { variant: "contained", color: "warning" },
    style: {
      backgroundColor: palette.warning.main,
      color: palette.text.white,
      borderColor: palette.warning.main,
      "&:hover": {
        backgroundColor: palette.warning.hover,
        borderColor: palette.warning.hover,
      },
      "&:active": {
        backgroundColor: palette.warning.active,
        borderColor: palette.warning.active,
      },
    },
  },
  {
    props: { variant: "contained", color: "error" },
    style: {
      backgroundColor: palette.error.main,
      color: palette.text.white,
      borderColor: palette.error.main,
      "&:hover": {
        backgroundColor: palette.error.hover,
        borderColor: palette.error.hover,
      },
      "&:active": {
        backgroundColor: palette.error.active,
        borderColor: palette.error.active,
      },
    },
  },
  {
    props: { variant: "contained", color: "success" },
    style: {
      backgroundColor: palette.success.main,
      color: palette.text.white,
      borderColor: palette.success.main,
      "&:hover": {
        backgroundColor: palette.success.hover,
        borderColor: palette.success.hover,
      },
      "&:active": {
        backgroundColor: palette.success.active,
        borderColor: palette.success.active,
      },
    },
  },
  {
    props: { variant: "contained", color: "secondary" },
    style: {
      backgroundColor: palette.secondary.main,
      color: palette.text.white,
      borderColor: palette.secondary.main,
      "&:hover": {
        backgroundColor: palette.secondary.hover,
        borderColor: palette.secondary.hover,
      },
      "&:active": {
        backgroundColor: palette.secondary.active,
        borderColor: palette.secondary.active,
      },
    },
  },
  {
    props: { variant: "contained", color: "dark" },
    style: {
      backgroundColor: palette.dark.main,
      color: palette.text.white,
      borderColor: palette.dark.main,
      "&:hover": {
        backgroundColor: palette.dark.hover,
        borderColor: palette.dark.hover,
      },
      "&:active": {
        backgroundColor: palette.dark.active,
        borderColor: palette.dark.active,
      },
    },
  },
  {
    props: { variant: "contained", color: "light" },
    style: {
      backgroundColor: palette.light.main,
      color: palette.text.main,
      borderColor: palette.light.main,
      "&:hover": {
        backgroundColor: palette.light.hover,
        borderColor: palette.light.hover,
      },
      "&:active": {
        backgroundColor: palette.light.active,
        borderColor: palette.light.active,
      },
    },
  },

  {
    props: { variant: "contained", color: "disabled" },
    style: {
      ...styleDisabled,
      "&:hover": {
        backgroundColor: palette.disabled.hover,
        borderColor: palette.disabled.hover,
        boxShadow: "unset",
      },
      "&:active": {
        backgroundColor: palette.disabled.active,
        borderColor: palette.disabled.active,
        boxShadow: "unset",
      },
    },
  },
  {
    props: { variant: "outlined" },
    style: {
      color: palette.text.main,
      borderColor: palette.border.main,
      "&:hover, &:active": {
        backgroundColor: "transparent",
        borderColor: palette.border.main,
      },
    },
  },
  {
    props: { variant: "outlined", color: "normal" },
    style: {
      color: palette.text.main,
      borderColor: palette.border.main,
      "&:hover, &:active": {
        backgroundColor: "transparent",
      },
    },
  },
  {
    props: { variant: "outlined", color: "darkgreen" },
    style: {
      color: palette.darkgreen.main,
      borderColor: "currentColor",
      "&:hover, &:active": {
        borderColor: palette.darkgreen.main,
        backgroundColor: palette.darkgreen.main,
        color: palette.common.white,
      },
    },
  },
  {
    props: { variant: "outlined", color: "primary" },
    style: {
      color: palette.primary.main,
      borderColor: "currentColor",
      "&:hover, &:active": {
        borderColor: palette.primary.main,
        backgroundColor: palette.primary.main,
        color: palette.common.white,
      },
    },
  },
  {
    props: { variant: "outlined", color: "dark" },
    style: {
      color: palette.dark.main,
      borderColor: "currentColor",
      "&:hover, &:active": {
        borderColor: palette.dark.main,
        backgroundColor: palette.dark.main,
        color: palette.common.white,
      },
    },
  },
  {
    props: { variant: "outlined", color: "disabled" },
    style: {
      color: palette.disabled.main,
      borderColor: "currentColor",
      cursor: "default",
      "&:hover, &:active": {
        borderColor: palette.disabled.main,
        backgroundColor: palette.disabled.main,
        color: palette.text.disabled,
      },
    },
  },
  {
    props: { variant: "outlined", color: "error" },
    style: {
      color: palette.error.main,
      borderColor: "currentColor",
      "&:hover, &:active": {
        borderColor: palette.error.main,
        backgroundColor: palette.error.main,
        color: palette.common.white,
      },
    },
  },
  {
    props: { variant: "outlined", color: "light" },
    style: {
      color: palette.light.main,
      borderColor: "currentColor",
      "&:hover, &:active": {
        borderColor: palette.light.main,
        backgroundColor: palette.light.main,
        color: palette.common.white,
      },
    },
  },
  {
    props: { variant: "outlined", color: "secondary" },
    style: {
      color: palette.secondary.main,
      borderColor: "currentColor",
      "&:hover, &:active": {
        borderColor: palette.secondary.main,
        backgroundColor: palette.secondary.main,
        color: palette.common.white,
      },
    },
  },
  {
    props: { variant: "outlined", color: "success" },
    style: {
      color: palette.success.main,
      borderColor: "currentColor",
      "&:hover, &:active": {
        borderColor: palette.success.main,
        backgroundColor: palette.success.main,
        color: palette.common.white,
      },
    },
  },
  {
    props: { variant: "outlined", color: "warning" },
    style: {
      color: palette.warning.main,
      borderColor: "currentColor",
      "&:hover, &:active": {
        borderColor: palette.warning.main,
        backgroundColor: palette.warning.main,
        color: palette.common.white,
      },
    },
  },
  {
    props: { variant: "text" },
    style: {
      color: palette.text.main,
      borderColor: "transparent",
      "&:hover, &:active": {
        backgroundColor: "transparent",
        borderColor: "transparent",
      },
    },
  },
  {
    props: { variant: "text", color: "normal" },
    style: {
      color: palette.text.main,
      borderColor: "transparent",
      "&:hover, &:active": {
        backgroundColor: "transparent",
        borderColor: "transparent",
      },
    },
  },
  {
    props: { variant: "text", color: "primary" },
    style: {
      color: palette.primary.main,
    },
  },
  {
    props: { variant: "text", color: "darkgreen" },
    style: {
      color: palette.darkgreen.main,
    },
  },
  {
    props: { variant: "text", color: "dark" },
    style: {
      color: palette.dark.main,
    },
  },
  {
    props: { variant: "text", color: "disabled" },
    style: {
      color: palette.disabled.main,
      cursor: "default",
    },
  },
  {
    props: { variant: "text", color: "error" },
    style: {
      color: palette.error.main,
    },
  },
  {
    props: { variant: "text", color: "light" },
    style: {
      color: palette.light.main,
    },
  },
  {
    props: { variant: "text", color: "secondary" },
    style: {
      color: palette.secondary.main,
    },
  },
  {
    props: { variant: "text", color: "success" },
    style: {
      color: palette.success.main,
    },
  },
  {
    props: { variant: "text", color: "warning" },
    style: {
      color: palette.warning.main,
    },
  },
];
export default {
  defaultProps: {},
  styleOverrides: {
    root: {
      textTransform: "none" as const,
      color: "inherit",
      ...defaultStyle,
    },
    sizeSmall: {
      ...smSize,
    },
    sizeMedium: {
      ...mdSize,
    },
    sizeLarge: {
      ...lgSize,
    },
    sizeXlarge: {
      ...xlSize,
    },
    sizeXxlarge: {
      ...xxlSize,
    },
  },
  variants: variants,
};
